import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, User, Mail, Calendar, Activity, Trophy, 
  Dumbbell, Apple, Moon, LogIn, UserX, UserCheck, Gift,
  RefreshCw, Clock, Target, TrendingUp, AlertTriangle, Crown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [premiumFeatures, setPremiumFeatures] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [togglingFeature, setTogglingFeature] = useState(null);

  const adminKey = sessionStorage.getItem('aura_admin_key');

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchPremiumFeatures();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const [userRes, activityRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/users/${userId}`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/user-activities/${userId}`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/user-stats/${userId}`, { headers: { 'X-Admin-Key': adminKey } })
      ]);
      
      setUser(userRes.data);
      setActivities(activityRes.data.activities || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumFeatures = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/premium-features/${userId}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setAvailableFeatures(res.data.available_features || []);
      setPremiumFeatures(res.data.enabled_features || []);
    } catch (error) {
      console.error('Failed to fetch premium features:', error);
    }
  };

  const togglePremiumFeature = async (featureId, enabled) => {
    setTogglingFeature(featureId);
    try {
      await axios.post(`${API}/api/admin/toggle-premium-feature`, 
        { user_id: userId, feature: featureId, enabled },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      if (enabled) {
        setPremiumFeatures(prev => [...prev, featureId]);
        toast.success(`${featureId.replace('_', ' ')} enabled`);
      } else {
        setPremiumFeatures(prev => prev.filter(f => f !== featureId));
        toast.success(`${featureId.replace('_', ' ')} disabled`);
      }
    } catch (error) {
      toast.error('Failed to toggle feature');
    } finally {
      setTogglingFeature(null);
    }
  };

  const handleImpersonate = async () => {
    try {
      const response = await axios.post(`${API}/api/admin/impersonate`, 
        { user_id: userId },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      localStorage.setItem('aura_token', response.data.token);
      localStorage.setItem('aura_impersonating', 'true');
      localStorage.setItem('aura_admin_key_backup', adminKey);
      
      toast.success('Impersonation started');
      window.open('/', '_blank');
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    
    const action = user.disabled ? 'enable' : 'disable';
    if (!window.confirm(`${action === 'enable' ? 'Enable' : 'Disable'} this account?`)) return;
    
    try {
      await axios.post(`${API}/api/admin/${action}-account`, 
        { email: user.email, reason: `${action}d by admin` },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success(`Account ${action}d`);
      fetchUserDetails();
    } catch (error) {
      toast.error(`Failed to ${action} account`);
    }
  };

  const handleGrantFreeAccess = async () => {
    if (!user) return;
    
    try {
      await axios.post(`${API}/api/admin/grant-free-access`, 
        { email: user.email, reason: 'Granted by admin' },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success('Free access granted');
      fetchUserDetails();
    } catch (error) {
      toast.error('Failed to grant access');
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      workout: <Dumbbell className="w-4 h-4 text-aura-primary" />,
      meal: <Apple className="w-4 h-4 text-green-400" />,
      recovery: <Moon className="w-4 h-4 text-purple-400" />,
      challenge: <Trophy className="w-4 h-4 text-amber-400" />,
      login: <LogIn className="w-4 h-4 text-blue-400" />,
      default: <Activity className="w-4 h-4 text-aura-text-muted" />
    };
    return icons[type] || icons.default;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">User Not Found</h2>
          <p className="text-aura-text-muted mb-4">This user may have been deleted.</p>
          <Link to="/admin/users">
            <Button className="bg-aura-primary">Back to Users</Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/users">
              <Button variant="ghost" className="text-aura-text-muted hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center">
                <span className="text-white font-bold text-2xl">
                  {(user.name || user.email)?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
                  {user.name || 'No Name'}
                </h1>
                <p className="text-aura-text-muted">{user.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${
                    user.role === 'athlete' ? 'bg-aura-primary/20 text-aura-primary' :
                    user.role === 'supporter' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-amber-500/20 text-amber-400'
                  } border-0`}>
                    {user.role}
                  </Badge>
                  {user.disabled ? (
                    <Badge className="bg-red-500/20 text-red-400 border-0">Disabled</Badge>
                  ) : user.free_access ? (
                    <Badge className="bg-green-500/20 text-green-400 border-0">Free Access</Badge>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleImpersonate}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login as User
            </Button>
            {!user.free_access && (
              <Button 
                onClick={handleGrantFreeAccess}
                variant="outline" 
                className="border-green-500/50 text-green-400"
              >
                <Gift className="w-4 h-4 mr-2" />
                Grant Access
              </Button>
            )}
            <Button 
              onClick={handleToggleStatus}
              variant="outline" 
              className={user.disabled 
                ? 'border-green-500/50 text-green-400' 
                : 'border-red-500/50 text-red-400'
              }
            >
              {user.disabled ? (
                <><UserCheck className="w-4 h-4 mr-2" /> Enable</>
              ) : (
                <><UserX className="w-4 h-4 mr-2" /> Disable</>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Dumbbell className="w-6 h-6 text-aura-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.totalWorkouts || 0}</p>
              <p className="text-xs text-aura-text-muted">Total Sessions</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Apple className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.totalMeals || 0}</p>
              <p className="text-xs text-aura-text-muted">Meals Logged</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.auraScore || 0}</p>
              <p className="text-xs text-aura-text-muted">Aura Score</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.currentStreak || 0}</p>
              <p className="text-xs text-aura-text-muted">Day Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="bg-aura-surface border border-white/10">
            <TabsTrigger value="activity" className="data-[state=active]:bg-aura-primary">
              Activity
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-aura-primary">
              Profile
            </TabsTrigger>
            <TabsTrigger value="premium" className="data-[state=active]:bg-aura-primary">
              <Crown className="w-4 h-4 mr-1" />
              Premium
            </TabsTrigger>
            <TabsTrigger value="usage" className="data-[state=active]:bg-aura-primary">
              Usage Stats
            </TabsTrigger>
          </TabsList>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider">
                  Activity Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length > 0 ? (
                  <div className="space-y-4">
                    {activities.map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-3 rounded-lg bg-white/5">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1">
                          <p className="text-white">{activity.description}</p>
                          <p className="text-xs text-aura-text-muted mt-1">
                            {activity.timestamp 
                              ? formatDistanceToNow(parseISO(activity.timestamp), { addSuffix: true })
                              : ''
                            }
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-aura-text-muted py-8">No activity recorded</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider">
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-aura-text-muted uppercase">Email</p>
                    <p className="text-white">{user.email}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-aura-text-muted uppercase">Role</p>
                    <p className="text-white capitalize">{user.role}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-aura-text-muted uppercase">Joined</p>
                    <p className="text-white">
                      {user.created_at ? format(parseISO(user.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-aura-text-muted uppercase">Last Active</p>
                    <p className="text-white">
                      {user.last_active 
                        ? formatDistanceToNow(parseISO(user.last_active), { addSuffix: true })
                        : 'Never'
                      }
                    </p>
                  </div>
                  {user.sport && (
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-aura-text-muted uppercase">Sport</p>
                      <p className="text-white capitalize">{user.sport}</p>
                    </div>
                  )}
                  {user.org_id && (
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-aura-text-muted uppercase">Organization</p>
                      <p className="text-white">{user.org_name || user.org_id}</p>
                    </div>
                  )}
                </div>

                {user.onboarding_data && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-white mb-3">Onboarding Data</h4>
                    <div className="p-3 rounded-lg bg-white/5">
                      <pre className="text-xs text-aura-text-muted overflow-auto">
                        {JSON.stringify(user.onboarding_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Premium Features Tab */}
          <TabsContent value="premium" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Premium Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-aura-text-muted text-sm mb-6">
                  Toggle premium features on/off for this user. Useful for beta testing before purchase.
                </p>
                <div className="space-y-4">
                  {availableFeatures.map((feature) => (
                    <div 
                      key={feature.id} 
                      className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{feature.name}</h4>
                        <p className="text-xs text-aura-text-muted mt-1">{feature.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${premiumFeatures.includes(feature.id) ? 'text-green-400' : 'text-aura-text-muted'}`}>
                          {premiumFeatures.includes(feature.id) ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={premiumFeatures.includes(feature.id)}
                          onCheckedChange={(checked) => togglePremiumFeature(feature.id, checked)}
                          disabled={togglingFeature === feature.id}
                          className="data-[state=checked]:bg-green-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {availableFeatures.length === 0 && (
                  <p className="text-center text-aura-text-muted py-8">Loading features...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usage Stats Tab */}
          <TabsContent value="usage" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider">
                  Feature Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-aura-primary">{stats?.workoutsThisWeek || 0}</p>
                    <p className="text-xs text-aura-text-muted">Sessions This Week</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-green-400">{stats?.mealsThisWeek || 0}</p>
                    <p className="text-xs text-aura-text-muted">Meals This Week</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-purple-400">{stats?.recoveryLogs || 0}</p>
                    <p className="text-xs text-aura-text-muted">Recovery Logs</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-amber-400">{stats?.challengesCompleted || 0}</p>
                    <p className="text-xs text-aura-text-muted">Challenges Done</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-blue-400">{stats?.loginCount || 0}</p>
                    <p className="text-xs text-aura-text-muted">Total Logins</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 text-center">
                    <p className="text-2xl font-bold text-white">{stats?.daysActive || 0}</p>
                    <p className="text-xs text-aura-text-muted">Days Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminUserDetailPage;
